import { makeObservable, reaction } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";
import { UnitNodeModel } from "@/models/nodes/UnitNodeModel";
import { isPowerOperator, isSupSubNode } from "@/models/utils/semanticChecks";
import { getRandomTailwindColor } from "@/utils/tailwindColors";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { TailwindColor } from "@/utils/tailwindColors";
import type {
  ClosingSymbol,
  OpeningSymbol,
  VariableNodeProps,
  VariableNodeConstructorProps,
} from "@corca/data";

export class VariableNodeModel extends BaseNodeModel<VariableNodeProps> implements VisitableNode {
  static type = "variable" as const;

  type = VariableNodeModel.type;

  static getVariableByValue(doc: DocumentModel, value?: string) {
    if (!value) {
      return undefined;
    }
    const variables = doc.getNodesByType(VariableNodeModel.type);
    return variables.find((node) => node.value === value);
  }

  static getVariableColor(
    doc: DocumentModel,
    props: VariableNodeConstructorProps,
    value?: string,
  ): TailwindColor {
    return (
      props.color ||
      VariableNodeModel.getVariableByValue(doc, value)?.color ||
      getRandomTailwindColor(value, "slate")
    );
  }

  protected fillRequiredProps(props: VariableNodeConstructorProps): VariableNodeProps {
    const color = VariableNodeModel.getVariableColor(this.doc, props, this.symbolNode?.value);
    return { ...props, color: props.color ?? color };
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<VariableNodeConstructorProps>>) {
    super(...args);

    makeObservable(this, {
      symbolNode: true,
      parameters: true,
      paramsAreEmpty: true,
      hasParams: true,
      value: true,
      unitNode: true,
      power: true,
    });

    this.onCleanup(
      reaction(
        () => this.hasParams,
        (hasParams) => {
          if (hasParams) {
            this.claimPowerOperator();
          } else {
            this.releasePowerOperator();
          }
        },
      ),
    );
  }

  static create(
    doc: DocumentModel,
    value: string | SymbolNodeModel,
    props: VariableNodeConstructorProps = {},
    nodes: NodeModel[] = [],
    id?: NodeId,
  ) {
    const symbolNode = typeof value === "string" ? doc.create(SymbolNodeModel, value) : value;
    const color = VariableNodeModel.getVariableColor(doc, props, symbolNode.value);

    return new VariableNodeModel(doc, id, { ...props, color }, [symbolNode, ...nodes]);
  }

  get isEditable(): boolean {
    return true;
  }

  get symbolNode() {
    return this.children.find((n) => n.is(SymbolNodeModel));
  }

  get parameters() {
    return this.children.filter((n) => n.is(InlineNodeModel));
  }

  get unitNode() {
    return this.children.find((n) => n.is(UnitNodeModel));
  }

  get power() {
    return this.children.find(isPowerOperator);
  }

  get hasParams(): boolean {
    return this.parameters.length > 0;
  }

  get paramsAreEmpty(): boolean {
    return this.parameters.every((param) => param.isEmpty);
  }

  get value(): string {
    return this.symbolNode?.props?.value || "";
  }

  set value(value: string) {
    if (this.symbolNode) {
      this.symbolNode.value = value;
    }
  }

  get labeledValue(): string {
    return this.symbolNode?.labeledValue || this.value;
  }

  get name(): string {
    return this.props.name || "";
  }

  set name(value: string) {
    this.updateProps({ name: value });
  }

  get color(): TailwindColor {
    return this.props.color;
  }

  set color(value: TailwindColor) {
    this.updateProps({ color: value });
  }

  get openingSymbol() {
    return this.props.openingSymbol || "(";
  }

  set openingSymbol(openingSymbol: OpeningSymbol | undefined) {
    this.updateProps({ openingSymbol });
  }

  get closingSymbol() {
    return this.props.closingSymbol || ")";
  }

  set closingSymbol(closingSymbol: ClosingSymbol | undefined) {
    this.updateProps({ closingSymbol });
  }

  private claimPowerOperator() {
    const [next] = this.nextSiblings;

    if (!this.power && isSupSubNode(next)) {
      this.prependChild(next);
    }
  }

  private releasePowerOperator() {
    if (this.power) {
      this.insertSiblingAfter(this.power);
    }
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitVariableNode(this);
  }
}
